import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Header from '@/components/Header'
import Footer from '@/components/Footer'

// ---------------------------------------------------------------------------
// Static region lists sourced from YED USPS region/state codes documentation
// ---------------------------------------------------------------------------

const US_REGIONS = [
    { code: 'AL', name: 'Alabama' },
    { code: 'AK', name: 'Alaska' },
    { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' },
    { code: 'CA', name: 'California' },
    { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' },
    { code: 'DE', name: 'Delaware' },
    { code: 'DC', name: 'District of Columbia' },
    { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' },
    { code: 'HI', name: 'Hawaii' },
    { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' },
    { code: 'IN', name: 'Indiana' },
    { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' },
    { code: 'KY', name: 'Kentucky' },
    { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' },
    { code: 'MD', name: 'Maryland' },
    { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' },
    { code: 'MN', name: 'Minnesota' },
    { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' },
    { code: 'MT', name: 'Montana' },
    { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' },
    { code: 'NH', name: 'New Hampshire' },
    { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' },
    { code: 'NY', name: 'New York' },
    { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' },
    { code: 'OH', name: 'Ohio' },
    { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' },
    { code: 'PA', name: 'Pennsylvania' },
    { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' },
    { code: 'SD', name: 'South Dakota' },
    { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' },
    { code: 'UT', name: 'Utah' },
    { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' },
    { code: 'WA', name: 'Washington' },
    { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' },
    { code: 'WY', name: 'Wyoming' },
]

const US_TERRITORIES = [
    { code: 'AS', name: 'American Samoa' },
    { code: 'GU', name: 'Guam' },
    { code: 'MH', name: 'Marshall Islands' },
    { code: 'FM', name: 'Micronesia' },
    { code: 'MP', name: 'Northern Mariana Islands' },
    { code: 'PW', name: 'Palau' },
    { code: 'PR', name: 'Puerto Rico' },
    { code: 'VI', name: 'U.S. Virgin Islands' },
]

const US_ARMED_FORCES = [
    { code: 'AA', name: 'Armed Forces Americas' },
    { code: 'AE', name: 'Armed Forces Europe' },
    { code: 'AP', name: 'Armed Forces Pacific' },
]

const CA_PROVINCES = [
    { code: 'AB', name: 'Alberta' },
    { code: 'BC', name: 'British Columbia' },
    { code: 'MB', name: 'Manitoba' },
    { code: 'NB', name: 'New Brunswick' },
    { code: 'NF', name: 'Newfoundland' },
    { code: 'NS', name: 'Nova Scotia' },
    { code: 'NT', name: 'Northwest Territories' },
    { code: 'NU', name: 'Nunavut' },
    { code: 'ON', name: 'Ontario' },
    { code: 'PE', name: 'Prince Edward Island' },
    { code: 'QC', name: 'Quebec' },
    { code: 'SK', name: 'Saskatchewan' },
    { code: 'YK', name: 'Yukon Territory' },
]

const selectClass = 'w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-yubico-green-400 outline-none bg-gray-50 transition-all text-sm font-medium'
const inputClass = 'w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-yubico-green-400 outline-none transition-all bg-gray-50 focus:bg-white'
const inputErrorClass = 'w-full px-4 py-3 rounded-xl border border-red-400 focus:ring-2 focus:ring-red-400 outline-none transition-all bg-red-50 focus:bg-white'

// YED requires Latin (ASCII) characters only in address fields.
// Returns the first offending character found, or null if clean.
function findNonAscii(value: string): string | null {
    for (const char of value) {
        if (char.codePointAt(0)! > 127) return char
    }
    return null
}

const ADDRESS_FIELDS: { key: string; label: string }[] = [
    { key: 'street_line1', label: 'Street Address' },
    { key: 'street_line2', label: 'Suite / Apartment' },
    { key: 'city', label: 'City' },
    { key: 'postal_code', label: 'Postal / ZIP Code' },
]

function ShippingPage() {
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)
    const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle')
    const [errorMsg, setErrorMsg] = useState('')
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

    // Country list fetched from backend (YED API proxy)
    const [countries, setCountries] = useState<{ name: string; code: string }[]>([])
    const [countriesLoading, setCountriesLoading] = useState(true)

    useEffect(() => {
        // Add meta robots tag to prevent indexing
        const meta = document.createElement('meta')
        meta.name = 'robots'
        meta.content = 'noindex, nofollow, noarchive'
        document.head.appendChild(meta)

        console.log('[ShippingPage] Component mounted at', new Date().toISOString())

        // Fetch country list from YED API (via backend proxy)
        fetch('/api/countries')
            .then(r => r.ok ? r.json() : Promise.reject(r.status))
            .then((data: { name: string; code: string }[]) => setCountries(data))
            .catch(err => console.error('[ShippingPage] Failed to load countries:', err))
            .finally(() => setCountriesLoading(false))

        return () => {
            if (document.head.contains(meta)) document.head.removeChild(meta)
            console.log('[ShippingPage] Component unmounted at', new Date().toISOString())
        }
    }, [])

    const [formData, setFormData] = useState({
        requested_by: '',
        recipient_firstname: '',
        recipient_lastname: '',
        recipient: '', // Company Name
        recipient_email: '',
        recipient_telephone: '',
        street_line1: '',
        street_line2: '',
        city: '',
        region: '',
        country_code_2: '',
        postal_code: '',
        delivery_type: 1, // 1: Normal, 2: Expedited
        product_id: 1, // Default to YubiKey 5 NFC
        inventory_id: 220, // Default to YaaS Advanced Primary
        customization_id: 'NZ2YZW' // Default found in contributor zip
    })

    const showRegion = formData.country_code_2 === 'US' || formData.country_code_2 === 'CA'

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { id, value } = e.target

        // Clear any field-level error when the user edits that field
        if (fieldErrors[id]) {
            setFieldErrors(prev => { const next = { ...prev }; delete next[id]; return next })
        }

        setFormData(prev => {
            const next = {
                ...prev,
                [id]: id === 'delivery_type' || id === 'product_id' || id === 'inventory_id' ? parseInt(value) : value
            }
            // Clear region when switching away from US / CA
            if (id === 'country_code_2' && value !== 'US' && value !== 'CA') {
                next.region = ''
            }
            return next
        })
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        // Validate address fields for non-ASCII characters before hitting the API.
        // YED rejects anything outside basic ASCII in address fields.
        const errors: Record<string, string> = {}
        for (const { key, label } of ADDRESS_FIELDS) {
            const val = (formData as any)[key] as string
            if (!val) continue
            const bad = findNonAscii(val)
            if (bad) {
                errors[key] = `${label} must use standard Latin characters only (found "${bad}"). Please replace accented or special characters.`
            }
        }
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors)
            return
        }

        setLoading(true)
        setStatus('idle')

        // Map to API structure
        const submissionPayload = {
            ...formData,
            shipment_items: [
                {
                    product_id: formData.product_id,
                    inventory_product_id: formData.inventory_id,
                    quantity: 1
                }
            ]
        }

        try {
            const response = await fetch('/api/shipping', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(submissionPayload),
            })

            const data = await response.json()

            if (response.ok) {
                setStatus('success')
            } else {
                setStatus('error')
                setErrorMsg(data.error || 'Failed to submit shipping information')
            }
        } catch (error) {
            setStatus('error')
            setErrorMsg('An unexpected error occurred. Please try again.')
            console.error('[Shipping] Submission error:', error)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <Header variant="default" />

            <main className="flex-grow flex items-center justify-center p-6 py-12">
                <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-8 border border-gray-100">
                    <div className="text-center mb-8">
                        <div className="inline-block p-3 bg-yubico-green-50 rounded-2xl mb-4">
                            <i className="iconoir-delivery-truck text-yubico-green-400 text-3xl"></i>
                        </div>
                        <h2 className="text-3xl font-bold text-gray-900">Request Your YubiKey</h2>
                        <p className="text-gray-500 mt-2">Secure hardware delivery powered by ACME Portal</p>
                    </div>

                    {status === 'success' ? (
                        <div className="text-center py-8">
                            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                <i className="iconoir-check-circle text-green-600 text-4xl"></i>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-3">Submission Successful</h3>
                            <p className="text-gray-500 mb-8 max-w-md mx-auto">
                                Your shipping request has been sent for approval. You will receive tracking information via email once it has been processed.
                            </p>
                            <div className="flex flex-col gap-3">
                                <button
                                    onClick={() => setStatus('idle')}
                                    className="w-full bg-yubico-green-400 hover:bg-yubico-green-500 text-white font-bold py-4 px-4 rounded-xl transition-all transform hover:scale-[1.02]"
                                >
                                    Create another FPR Request
                                </button>
                                <button
                                    onClick={() => navigate('/admin')}
                                    className="w-full py-3 text-gray-500 hover:text-gray-900 text-sm font-bold transition-colors"
                                >
                                    Return to Dashboard
                                </button>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Requester */}
                                <div className="md:col-span-2 space-y-1">
                                    <label htmlFor="requested_by" className="block text-sm font-bold text-gray-700">Requested By (Yubico Employee)</label>
                                    <input
                                        type="email"
                                        id="requested_by"
                                        value={formData.requested_by}
                                        onChange={handleChange}
                                        className={inputClass}
                                        placeholder="admin@acme-corp.com"
                                        required
                                    />
                                </div>

                                {/* Personal Info */}
                                <div className="space-y-1">
                                    <label htmlFor="recipient_firstname" className="block text-sm font-bold text-gray-700">First Name</label>
                                    <input
                                        type="text"
                                        id="recipient_firstname"
                                        value={formData.recipient_firstname}
                                        onChange={handleChange}
                                        className={inputClass}
                                        placeholder="Jane"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label htmlFor="recipient_lastname" className="block text-sm font-bold text-gray-700">Last Name</label>
                                    <input
                                        type="text"
                                        id="recipient_lastname"
                                        value={formData.recipient_lastname}
                                        onChange={handleChange}
                                        className={inputClass}
                                        placeholder="Doe"
                                    />
                                </div>
                                <div className="md:col-span-2 space-y-1">
                                    <div className="flex justify-between items-baseline">
                                        <label htmlFor="recipient" className="block text-sm font-bold text-gray-700">Company Name</label>
                                        <span className="text-xs text-gray-400">{formData.recipient.length}/20</span>
                                    </div>
                                    <input
                                        type="text"
                                        id="recipient"
                                        value={formData.recipient}
                                        onChange={handleChange}
                                        className={inputClass}
                                        placeholder="ACME Corp"
                                        maxLength={20}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label htmlFor="recipient_email" className="block text-sm font-bold text-gray-700">Work Email</label>
                                    <input
                                        type="email"
                                        id="recipient_email"
                                        value={formData.recipient_email}
                                        onChange={handleChange}
                                        className={inputClass}
                                        placeholder="jane.doe@acme.com"
                                        required
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label htmlFor="recipient_telephone" className="block text-sm font-bold text-gray-700">Telephone</label>
                                    <input
                                        type="tel"
                                        id="recipient_telephone"
                                        value={formData.recipient_telephone}
                                        onChange={handleChange}
                                        className={inputClass}
                                        placeholder="+1 555-0199"
                                        required
                                    />
                                </div>

                                {/* Address Details */}
                                <div className="md:col-span-2 space-y-1">
                                    <label htmlFor="street_line1" className="block text-sm font-bold text-gray-700">Street Address</label>
                                    <input
                                        type="text"
                                        id="street_line1"
                                        value={formData.street_line1}
                                        onChange={handleChange}
                                        className={fieldErrors.street_line1 ? inputErrorClass : inputClass}
                                        placeholder="123 Yubico Way"
                                        required
                                    />
                                    {fieldErrors.street_line1 && (
                                        <p className="text-xs text-red-600 mt-1">{fieldErrors.street_line1}</p>
                                    )}
                                </div>
                                <div className="md:col-span-2 space-y-1">
                                    <label htmlFor="street_line2" className="block text-sm font-bold text-gray-700">Suite / Apartment (Optional)</label>
                                    <input
                                        type="text"
                                        id="street_line2"
                                        value={formData.street_line2}
                                        onChange={handleChange}
                                        className={fieldErrors.street_line2 ? inputErrorClass : inputClass}
                                        placeholder="Suite 456"
                                    />
                                    {fieldErrors.street_line2 && (
                                        <p className="text-xs text-red-600 mt-1">{fieldErrors.street_line2}</p>
                                    )}
                                </div>
                                <div className="space-y-1">
                                    <label htmlFor="city" className="block text-sm font-bold text-gray-700">City</label>
                                    <input
                                        type="text"
                                        id="city"
                                        value={formData.city}
                                        onChange={handleChange}
                                        className={fieldErrors.city ? inputErrorClass : inputClass}
                                        placeholder="Palo Alto"
                                        required
                                    />
                                    {fieldErrors.city && (
                                        <p className="text-xs text-red-600 mt-1">{fieldErrors.city}</p>
                                    )}
                                </div>
                                <div className="space-y-1">
                                    <label htmlFor="postal_code" className="block text-sm font-bold text-gray-700">Postal / ZIP Code</label>
                                    <input
                                        type="text"
                                        id="postal_code"
                                        value={formData.postal_code}
                                        onChange={handleChange}
                                        className={fieldErrors.postal_code ? inputErrorClass : inputClass}
                                        placeholder="94301"
                                        required
                                    />
                                    {fieldErrors.postal_code && (
                                        <p className="text-xs text-red-600 mt-1">{fieldErrors.postal_code}</p>
                                    )}
                                </div>

                                {/* Country dropdown — populated from YED API */}
                                <div className="md:col-span-2 space-y-1">
                                    <label htmlFor="country_code_2" className="block text-sm font-bold text-gray-700">Country</label>
                                    <select
                                        id="country_code_2"
                                        value={formData.country_code_2}
                                        onChange={handleChange}
                                        className={selectClass}
                                        required
                                        disabled={countriesLoading}
                                    >
                                        <option value="">
                                            {countriesLoading ? 'Loading countries…' : '— Select a country —'}
                                        </option>
                                        {countries.map(c => (
                                            <option key={c.code} value={c.code}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* State / Region dropdown — only for US and Canada */}
                                {showRegion && (
                                    <div className="md:col-span-2 space-y-1">
                                        <label htmlFor="region" className="block text-sm font-bold text-gray-700">
                                            {formData.country_code_2 === 'CA' ? 'Province / Territory' : 'State / Region'}
                                        </label>
                                        <select
                                            id="region"
                                            value={formData.region}
                                            onChange={handleChange}
                                            className={selectClass}
                                            required
                                        >
                                            <option value="">— Select a {formData.country_code_2 === 'CA' ? 'province' : 'state'} —</option>
                                            {formData.country_code_2 === 'CA' ? (
                                                <optgroup label="Provinces &amp; Territories">
                                                    {CA_PROVINCES.map(r => (
                                                        <option key={r.code} value={r.code}>{r.name} ({r.code})</option>
                                                    ))}
                                                </optgroup>
                                            ) : (
                                                <>
                                                    <optgroup label="States">
                                                        {US_REGIONS.map(r => (
                                                            <option key={r.code} value={r.code}>{r.name} ({r.code})</option>
                                                        ))}
                                                    </optgroup>
                                                    <optgroup label="Territories">
                                                        {US_TERRITORIES.map(r => (
                                                            <option key={r.code} value={r.code}>{r.name} ({r.code})</option>
                                                        ))}
                                                    </optgroup>
                                                    <optgroup label="Armed Forces">
                                                        {US_ARMED_FORCES.map(r => (
                                                            <option key={r.code} value={r.code}>{r.name} ({r.code})</option>
                                                        ))}
                                                    </optgroup>
                                                </>
                                            )}
                                        </select>
                                    </div>
                                )}

                                {/* Shipment Options */}
                                <div className="space-y-1">
                                    <label htmlFor="product_id" className="block text-sm font-bold text-gray-700">Select Security Key</label>
                                    <select
                                        id="product_id"
                                        value={formData.product_id}
                                        onChange={handleChange}
                                        className={selectClass}
                                    >
                                        <option value={1}>YubiKey 5 NFC</option>
                                        <option value={2}>YubiKey 5 Nano</option>
                                        <option value={3}>YubiKey 5C</option>
                                        <option value={4}>YubiKey 5C Nano</option>
                                        <option value={29}>YubiKey 5C NFC</option>
                                        <option value={5}>YubiKey 5Ci</option>
                                    </select>
                                </div>
                                <div className="space-y-1">
                                    <label htmlFor="inventory_id" className="block text-sm font-bold text-gray-700">Inventory / YaaS Type</label>
                                    <select
                                        id="inventory_id"
                                        value={formData.inventory_id}
                                        onChange={handleChange}
                                        className={selectClass}
                                    >
                                        <option value={220}>YubiKey as a Service - Advanced - Primary</option>
                                        <option value={222}>YubiKey as a Service - Advanced - Primary - Replacement</option>
                                    </select>
                                </div>
                                <div className="space-y-1">
                                    <label htmlFor="delivery_type" className="block text-sm font-bold text-gray-700">Delivery Method</label>
                                    <select
                                        id="delivery_type"
                                        value={formData.delivery_type}
                                        onChange={handleChange}
                                        className={selectClass}
                                    >
                                        <option value={1}>Standard Shipping (Normal)</option>
                                        <option value={2}>Expedited Shipping (Fast)</option>
                                    </select>
                                </div>
                                <div className="space-y-1">
                                    <label htmlFor="customization_id" className="block text-sm font-bold text-gray-700">Customization ID</label>
                                    <input
                                        type="text"
                                        id="customization_id"
                                        disabled
                                        value={formData.customization_id}
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-200 text-gray-500 outline-none text-sm font-medium cursor-not-allowed"
                                    />
                                </div>
                            </div>

                            {status === 'error' && (
                                <div className="p-4 bg-red-50 text-red-700 rounded-xl text-sm border border-red-100">
                                    {errorMsg}
                                </div>
                            )}

                            <div className="pt-4 flex flex-col gap-3">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className={`w-full font-bold py-4 px-4 rounded-xl transition-all flex items-center justify-center gap-2 transform hover:scale-[1.02] active:scale-[0.98] ${loading
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-yubico-green-400 hover:bg-yubico-green-500 text-white shadow-lg shadow-yubico-green-400/20'
                                        }`}
                                >
                                    {loading ? (
                                        <>
                                            <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                            <span>Processing Request...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>Submit Shipping Request</span>
                                            <i className="iconoir-arrow-right text-lg"></i>
                                        </>
                                    )}
                                </button>

                                <button
                                    type="button"
                                    onClick={() => navigate('/admin')}
                                    className="w-full py-3 text-gray-500 hover:text-gray-900 text-sm font-bold transition-colors"
                                >
                                    Cancel and Return
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </main>

            <Footer variant="default" />
        </div>
    )
}

export default ShippingPage
